const TAG_MATCHER = /^([A-Z]{2}){1}(\d+)?([A-Z]{1,2})?/;

export const cleanDeviceBarcode = (code: string): string =>
  code.toUpperCase().replace(new RegExp('[: ]', 'g'), '').trim();

export function isShelfTagCode(code: string): boolean {
  try {
    parseShelfTag(code);
    return true;
  } catch (error) {
    return false;
  }
}

export type ShelfTagFormat =
  | 'compactSideJoined' // GR12L-2
  | 'compactNoSide' // B12-1 / GR12-1
  | 'dashedNumericSide' // 12-L-3 (side is L/R in source, mapped to A/B in sideCode)
  | 'meijer' // E-12-3 (side derived from sequence parity)
  | 'dashed' // GR-1-L-2
  | 'prefixDashed'; // BR-GR-1-L-2

export interface ParsedShelfTag {
  zone: string;
  aisle: number;
  sideCode: string;
  sequence: number;
  shelfTag: string;
  prefix?: string;
  /**
   * Which regex branch produced this parse. Set so formatShelfTag can re-emit the tag
   * in its original shape. Not all callers need this, so it's optional for backwards compat.
   */
  format?: ShelfTagFormat;
  /**
   * Width of the zero-padded aisle number in the source string (e.g., "01" => 2).
   * Used by formatShelfTag to preserve padding when re-emitting.
   */
  aisleWidth?: number;
  /**
   * Width of the zero-padded sequence number in the source string (e.g., "02" => 2).
   * Used by formatShelfTag to preserve padding when re-emitting.
   */
  sequenceWidth?: number;
  /**
   * For dashedNumericSide ('12-L-3'), parseShelfTag maps L->A, R->B. The source side
   * character is stored here so formatShelfTag can round-trip.
   */
  rawSide?: string;
}

const inventoryTagsPrefixes = ['TS', 'BR'];

// Port from backend function called parse_tag_name
export const parseShelfTag = (tagName: string): ParsedShelfTag => {
  const parts = tagName.split('-');
  // Definite-assignment assertions: not every parse branch sets zone/side (e.g. '12-L-3'
  // has no zone), and the original runtime behavior returns undefined in those cases.
  let zone!: string, aisleNumber: number, side!: string, sequence: number;
  let format: ShelfTagFormat;
  let aisleWidth: number | undefined;
  let sequenceWidth: number | undefined;
  let rawSide: string | undefined;

  if (parts.length > 1 && inventoryTagsPrefixes.includes(parts[0])) {
    try {
      return {
        ...parseShelfTag(parts.slice(1).join('-')),
        shelfTag: tagName,
        prefix: parts[0],
      };
    } catch (error: unknown) {}
  }

  if (parts.length === 2) {
    sequence = parseInt(parts[1], 10);
    sequenceWidth = parts[1].length;
    if (parts[0].substring(0, 2).match(/^[A-Z]+$/) && parts[0].match(TAG_MATCHER)) {
      // GR12L-2
      const matchParts = parts[0].match(TAG_MATCHER)!;
      zone = matchParts[1];
      aisleNumber = parseInt(matchParts[2], 10);
      aisleWidth = matchParts[2]?.length;
      side = matchParts[3];
      format = side ? 'compactSideJoined' : 'compactNoSide';
    } else {
      // B12-1
      let index = 0;
      for (let i = 0; i < parts[0].length; i++) {
        const c = parts[0][i];
        if (!c.match(/^[A-Z]+$/)) {
          break;
        }
        index = i;
      }
      if (!parts[0].length) {
        throw Error(`Unrecognized format for ${tagName}`);
      }
      zone = parts[0].substring(0, index + 1);
      const aisleStr = parts[0].substring(index + 1);
      aisleNumber = parseInt(aisleStr, 10);
      aisleWidth = aisleStr.length;
      format = 'compactNoSide';
    }
  } else if (parts.length === 3) {
    // 12-L-3
    if (/^\d+$/.test(parts[0]) && /^\d+$/.test(parts[2])) {
      aisleNumber = parseInt(parts[0], 10);
      aisleWidth = parts[0].length;
      rawSide = parts[1];
      side =
        parts[1].match(/^[A-Z]+$/) && parts[1].length <= 3
          ? parts[1] === 'L'
            ? 'A'
            : 'B'
          : parts[1];
      sequence = parseInt(parts[2], 10);
      sequenceWidth = parts[2].length;
      format = 'dashedNumericSide';
    } else if (/^[A-Z]$/.test(parts[0]) && /^\d+$/.test(parts[1]) && /^\d+$/.test(parts[2])) {
      // E-12-3 (meijer format)
      zone = parts[0];
      aisleNumber = parseInt(parts[1], 10);
      aisleWidth = parts[1].length;
      sequence = parseInt(parts[2], 10);
      sequenceWidth = parts[2].length;
      side = sequence % 2 === 0 ? 'L' : 'R';
      format = 'meijer';
    } else {
      throw Error(`Unrecognized format for ${tagName}`);
    }
  } else if (parts.length === 4) {
    // GR-1-L-2
    if (!/^\d+$/.test(parts[1]) || !/^\d+$/.test(parts[3])) {
      throw Error(`Unrecognized format for ${tagName}`);
    }
    zone = parts[0];
    aisleNumber = parseInt(parts[1], 10);
    aisleWidth = parts[1].length;
    side = parts[2];
    sequence = parseInt(parts[3], 10);
    sequenceWidth = parts[3].length;
    format = 'dashed';
  } else if (parts.length === 5 && inventoryTagsPrefixes.includes(parts[0])) {
    // BR-GR-1-L-2
    if (!/^\d+$/.test(parts[2]) || !/^\d+$/.test(parts[4])) {
      throw Error(`Unrecognized format for ${tagName}`);
    }
    zone = parts[1];
    aisleNumber = parseInt(parts[2], 10);
    aisleWidth = parts[2].length;
    side = parts[3];
    sequence = parseInt(parts[4], 10);
    sequenceWidth = parts[4].length;
    format = 'prefixDashed';
  } else {
    throw Error(`Unrecognized format for ${tagName}`);
  }

  if (Number.isNaN(aisleNumber) || Number.isNaN(sequence)) {
    throw Error(`Unrecognized format for ${tagName}`);
  }

  if ((zone && !zone?.match(/^[A-Z]+$/)) || (side && !side?.match(/^[A-Z]+$/))) {
    throw Error(`Unrecognized format for ${tagName}`);
  }

  return {
    zone,
    aisle: aisleNumber,
    sideCode: side,
    sequence,
    shelfTag: tagName,
    format,
    aisleWidth,
    sequenceWidth,
    rawSide,
  };
};

export type ModInconsistencyType = 'gap' | 'location' | 'direction';

export interface ModInconsistency {
  type: ModInconsistencyType;
}

export type FirstLastValidationReason = 'unparseable' | 'location' | 'sequenceDistance';

export interface FirstLastValidationResult {
  valid: boolean;
  reason?: FirstLastValidationReason;
  /**
   * Full ordered list of tags from first to last, length === totalModsExpected.
   * Only set when valid === true. Walks ascending or descending based on which
   * sequence number is higher, so the user can scan in either direction.
   */
  expandedTags?: string[];
}

const tryParseShelfTag = (tag: string): ParsedShelfTag | null => {
  try {
    return parseShelfTag(tag);
  } catch {
    return null;
  }
};

export const hasLocationMismatch = (baseline: ParsedShelfTag, newTag: ParsedShelfTag): boolean => {
  if (baseline.zone !== undefined && newTag.zone !== undefined && baseline.zone !== newTag.zone) {
    return true;
  }

  if (
    baseline.aisle !== undefined &&
    newTag.aisle !== undefined &&
    baseline.aisle !== newTag.aisle
  ) {
    return true;
  }

  if (
    baseline.sideCode !== undefined &&
    newTag.sideCode !== undefined &&
    baseline.sideCode !== newTag.sideCode
  ) {
    return true;
  }

  if (
    baseline.prefix !== undefined &&
    newTag.prefix !== undefined &&
    baseline.prefix !== newTag.prefix
  ) {
    return true;
  }

  return false;
};

const hasNewSequenceGap = (existingSequences: number[], newSequence: number): boolean => {
  if (existingSequences.length === 0) {
    return false;
  }

  const min = Math.min(...existingSequences);
  const max = Math.max(...existingSequences);

  if (newSequence > min && newSequence < max) {
    return false;
  }

  if (newSequence <= min) {
    return min - newSequence > 1;
  }

  return newSequence - max > 1;
};

const hasDirectionBreak = (existingSequences: number[], newSequence: number): boolean => {
  if (existingSequences.length < 2) {
    return false;
  }

  const min = Math.min(...existingSequences);
  const max = Math.max(...existingSequences);

  const isInGap = newSequence > min && newSequence < max;

  if (isInGap) {
    return false;
  }

  const last = existingSequences[existingSequences.length - 1];
  const secondLast = existingSequences[existingSequences.length - 2];

  if (Math.abs(last - secondLast) !== 1) {
    return false;
  }

  const previousDirection = last - secondLast;
  const newDirection = newSequence - last;

  if (previousDirection > 0) {
    return newDirection < 0;
  }

  if (previousDirection < 0) {
    return newDirection > 0;
  }

  return false;
};

export const detectModInconsistency = (
  newModTag: string,
  existingModTags: string[]
): ModInconsistency | null => {
  if (existingModTags.length === 0) {
    return null;
  }

  const newParsed = tryParseShelfTag(newModTag);
  if (!newParsed) {
    return null;
  }

  const existingParsed = existingModTags.map(tryParseShelfTag);

  if (existingParsed.some((p) => p === null)) {
    return null;
  }

  const baseline = existingParsed[existingParsed.length - 1] as ParsedShelfTag;

  if (hasLocationMismatch(baseline, newParsed)) {
    return { type: 'location' };
  }

  const sameLocationSequences = (existingParsed as ParsedShelfTag[])
    .filter((p) => !hasLocationMismatch(baseline, p))
    .map((p) => p.sequence);

  if (hasNewSequenceGap(sameLocationSequences, newParsed.sequence)) {
    return { type: 'gap' };
  }

  if (hasDirectionBreak(sameLocationSequences, newParsed.sequence)) {
    return { type: 'direction' };
  }

  return null;
};
